/* eslint-disable max-lines */
import { useEffect } from 'react'
import sendTrackData from '@jushuitan/jst-track'
import Cookies from 'js-cookie'
import Qs from 'qs'
import { getVisitorId } from '@/utils/fingerprint'

import { getMenuData } from '@ant-design/pro-layout'
import { getMatchMenu } from '@umijs/route-utils'
import Logger from 'gyl-common/tools/logger'
import { createLocation } from 'gyl-common/tools/logger/utils/url'
import { omit } from 'gyl-common/tools/logger/utils/obj'
import {
    markedRules,
    resolveRelatedInfo,
    RELATED_INFO_RULES,
    overrideCreatePortal,
} from 'gyl-common/tools/logger/config/antd'
import { appCode, isSingleAppDomain } from '@/utils/singleApp'
import { cookiesDomain, getCoIdAndUid, getCoIdAndUidAsync } from '@/utils/token'
import { captureException } from '@/utils/track'
import { getOrigin, isNumber } from '@/utils/utils'
import { match } from '@/libs/path-to-regexp'

overrideCreatePortal()

export const setSource = source => {
    Cookies.set('__log_source', source, {
        expires: 7,
        domain: cookiesDomain,
    })
}

export const getSource = () => {
    if (isSingleAppDomain) {
        return `MARKET_${appCode}`
    }

    return Cookies.get('__log_source') || ''
}

export const getPage = () => {
    return omit(window.Jpage || {}, ['t', 'token', 'erpToken', 'access_token'], true)
}

const resolveSource = location => {
    return (location.query && location.query.ffrom) || ''
}

const getReferrer = () => {
    return document.referrer || ''
}

const getFullPath = path => {
    return `${getOrigin()}${path}`
}

// 处理路由信息，params传参需要处理，后台不要
const transLocation = location => {
    const route = getRouteByLocation(location)

    // 路由表中匹配到
    if (route) {
        const urlMatch = match(route.path)
        const { params } = urlMatch(location.pathname)

        // 是否有id相关的参数，只处理含有id的路由
        const hasIdParams = params && Object.keys(params).some(v => v.includes('id') || v.includes('Id'))

        if (hasIdParams) {
            return {
                ...location,
                pathname: route.path || location.pathname,
                query: {
                    ...params,
                    ...location.query,
                },
            }
        }
    }

    return location
}

export const setPage = originLocation => {
    const page = getPage()

    const obj = {}

    const location = transLocation(originLocation)

    const source = resolveSource(location)

    // 记录source
    if (source) {
        setSource(source)
    }

    if (page.screen) {
        Object.assign(obj, page, {
            preScreen: page.screen,
            shortPreScreen: page.shortScreen,
            preParam: page.param,
            screen: getFullPath(location.pathname),
            shortScreen: location.pathname,
            param: location.query,
            source,
        })
    } else {
        const { origin, pathname, query } = transLocation(createLocation(getReferrer()))

        Object.assign(obj, page, {
            preScreen: `${origin}${pathname}`,
            shortPreScreen: pathname,
            preParam: query,
            screen: getFullPath(location.pathname),
            shortScreen: location.pathname,
            param: location.query,
            source,
        })
    }

    window.Jpage = obj
}

// 数据埋点中间件
export const sendTrackDataMid = async ({ ...params }) => {
    const [visitorId, userInfo] = await Promise.all([getVisitorId(), getCoIdAndUidAsync()])

    const { coIdWidthSymbol, uidWidthSymbol } = userInfo || {}

    return sendTrackData({
        ...params,
        device_id: visitorId,
        co_id: coIdWidthSymbol,
        user_id: uidWidthSymbol,
    })
}

/*  ================ 数据埋点  =============== */

// 统计日志的一些行为变量
export const BEHAVIABLE = {
    COPY_INVITE_URL: 'copy_invite_url', // 复制邀请链接
    DOWN_INVITE_POSTER: 'download_invite_poster', // 下载邀请海报
    COPY_SPREAD_URL: 'copy_spread_url', // 复制推广链接
    COPY_GROUPMAINFEST_URL: 'copy_groupManifest_url', // 复制盘货表链接（供应商）
    DOWN_GROUPMAINFEST_POSTER: 'download_groupManifest_poster', // 下载盘货表海报（供应商）
    VIEW_SELLER_PAGE: 'view_seller_page', // 浏览商家展架（分销商）
    USE_FORECAST_CATEGORY: 'use_forecast_category', // 使用预测类目
    USE_ERPSKU_FILL: 'use_erpsku_fill', // 手工新增商品快捷导入ERP的SKU信息
    USE_ERPSKU_NOIMG_FILL: 'use_erpsku_noimg_fill', // 手工新增商品快捷导入ERP的SKU信息(不带图)
    USE_FORECAST_CATEGORY_PROPERTY: 'use_forecast_category_property' // 使用预测类目属性
}

const APP_KEY = `${window.PROJECT_CONFIG.defaultSettings.logAppKey.toLowerCase()}`

const APP_VERSION = window.APP_VERSION

// pathname白名单，页面pv需要在该页面主动记录
export const JST_PATHNAME_EXCLUDES = ['/jumpH5/index', '/jumpPage/index', '/j']

const ENABLE_AUTO_LOG_EVENT = [
    PROJECT_CONFIG.channelTerminalName,
    PROJECT_CONFIG.supplierTerminalName,
    PROJECT_CONFIG.towerTerminalName,
    PROJECT_CONFIG.helpTerminalName,
    PROJECT_CONFIG.accountTerminalName,
    PROJECT_CONFIG.darenTerminalName,
    PROJECT_CONFIG.adminTerminalName,
].includes(PROJECT_CONFIG.terminalName)

const getRouteByLocation = location => {
    const { menuData } = getMenuData(PROJECT_CONFIG.routes) || []

    return getMatchMenu(location.pathname || '/', menuData).pop()
}

// 获取顶层容器
const getContainer = () => {
    const { hostInfo } = window?.hostInstance?.getEnvInfo() || {}

    const { isInErp, isInEpaas } = hostInfo || {}

    // 注意顺序，优先判断iframe内嵌，取顶层容器
    if (isInErp) {
        return 'jst-erp'
    }

    if (isInEpaas) {
        return 'jst-epaas'
    }

    return ''
}

export const logger = new Logger({
    autoLogEvent: ENABLE_AUTO_LOG_EVENT,
    markedRules,
    resolveRelatedInfo(element) {
        return resolveRelatedInfo(element, {
            rules: RELATED_INFO_RULES.concat({
                className: ['antd-pro-layouts-custom-pro-layout-index-menuContainer'],
                info: {
                    title: '菜单',
                },
            }),
        })
    },
    ignorePvPathname: JST_PATHNAME_EXCLUDES,
    ignorePageQueryKeys: ['t', 'token', 'erpToken', 'access_token'],
    sendData: sendTrackDataMid,
    context() {
        const { coId, uid } = getCoIdAndUid()

        return {
            app_key: APP_KEY,
            // jst-erp = ERP、
            // jst-gyl-supplier = 供应链供应商侧
            // jst-gyl-channel = 供应链分销商侧、
            // jst-epaas = EPaas
            container: getContainer(), // 暂时只增加container
            // app_key_v2: window.PROJECT_CONFIG.logAppKey,
            user_id: uid,
            co_id: coId,
            app_version: APP_VERSION,
            is_track_prod: PROJECT_CONFIG.mode === 'prod',
        }
    },
    trackError({ elemXpath, exception }) {
        captureException(exception, {
            anchor: 'jstLogGlobalEvent',
            extra: {
                xpath: elemXpath,
            },
        })
    },
    transText(text, parents) {
        const delText = v => {
            if (typeof v !== 'string') {
                return ''
            }

            return v.replace(/(资料编码|商品编码)：([\S]+)/g, (w, $0) => ` ${$0}：***`).replace(/[(（]\d+[）)]/g, '')
        }

        return parents
            .map(v => v.title)
            .concat(text)
            .filter(v => v && !isNumber(v))
            .map(v => delText(v))
            .join('_')
    },
})

export const jstLogPv = (...args) => logger.pv(...args)

export const jstLogEvent = ({ element, event_id, args = {}, ...params }) => {
    try {
        const data = {
            ...params,
            event_id: event_id || '3001',
            element,
            element_args: args,
        }

        logger.event(data)
    } catch (e) {
        console.error('catchError', e)
    }
}

// 界面日志记录
export const jstDevLogEvent = ({ element, args, ...params }) => {
    try {
        const data = {
            ...params,
            event_id: '4001',
            element,
            element_args: args,
        }

        logger.event(data)
    } catch (e) {
        console.error('catchError', e)
    }
}

/*
 * 弹层日志虚拟路径hook
 * 抽屉打开需要记录pv @放翁
 * */
export const usePopVirtualRouteLog = config => {
    const { visible, name, params } = {
        ...(config || {}),
    }

    useEffect(() => {
        if (visible) {
            logger.event({
                page: `/virtual/${name}`,
                page_args: params,
                refer: location.pathname,
                refer_args: Qs.parse(location.search, {
                    ignoreQueryPrefix: true,
                }),
                event_id: '2001',
            })
        }
    }, [visible])
}

// 搜索事件args为数组
const isObj = v => Object.prototype.toString.call(v) === '[object Object]'

// 数字范围
const NUMBER_KEYS = ['stock', 'qty', 'weight']

// 不过滤/有/无
const FILTER_KEYS = ['buyerMessage', 'remark']

// 检查是否有效
const checkIsValid = target => {
    // number|boolean
    if (typeof target.v === 'number' || typeof target.v === 'boolean') {
        return true
    }

    // 非空字符串
    if (typeof target.v === 'string' && target.v) {
        return true
    }

    // 非空数组
    if (Array.isArray(target.v) && target.v.length) {
        return true
    }

    // 非空对象
    if (isObj(target.v) && Object.keys(target.v).length) {
        // 组合项
        if (target.v.hasOwnProperty('leftValue') || target.v.hasOwnProperty('rightValue')) {
            // 特殊字段没法子，只能通过字段名判断了

            // 数字范围
            if (NUMBER_KEYS.includes(target.k)) {
                return (
                    checkIsValid({
                        v: target.v.leftValue,
                    }) ||
                    checkIsValid({
                        v: target.v.rightValue,
                    })
                )
            }

            // 不过滤/有/无
            if (FILTER_KEYS.includes(target.k)) {
                return (
                    checkIsValid({
                        v: target.v.rightValue,
                    }) || String(target.v.leftValue) === '1' // 无时类型为1
                )
            }

            return checkIsValid({
                v: target.v.rightValue,
            })
        }

        return true
    }

    return false
}

const transSearchArgs = args => {
    if (isObj(args)) {
        return Object.keys(args)
            .map(v => ({
                k: v,
                v: args[v],
            }))
            .filter(checkIsValid)
    }

    return []
}

// 搜索事件埋点
export const jstLogSearch = ({ args, ...rest }) => {
    jstLogEvent({
        element: 'search',
        args: transSearchArgs(args),
        ...rest,
    })
}

// 搜索校验不通过事件埋点
export const jstLogSearchValidateFieldsError = ({ args, ...rest }) => {
    jstLogEvent({
        element: 'searchValidateFieldsError',
        args,
        ...rest,
    })
}
export const useLogAd = logData => {
    // 广告位的曝光数据埋点
    useEffect(() => {
        jstLogEvent({
            element: 'ad_view',
            event_id: '3002',
            args: logData,
        })
    }, [])

    // 广告位点击数据埋点
    const logAdClick = data => {
        jstLogEvent({
            element: 'ad_click',
            event_id: '3001',
            args: data,
        })
    }
    return [logAdClick]
}
// AI 生成（AI：Codex，模型：GPT-5-Codex，时间：2026-03-05 15:20）：移除对旧 REQUEST_CACHE_getUserInfo 结构的依赖。
